import { formatDuration, intervalToDuration } from "date-fns";
import type { DateRange } from "react-day-picker";
import { DateTime } from "luxon";
import humanizeDuration, { type Unit } from "humanize-duration";
import type { AxiosInstance } from "axios";

/**
 * Returns the smallest "nice" number >= val (e.g., 1, 2, 5, 10, 20, 50, 100...).
 * Use for chart domain max so the highest tick aligns with the top of the chart.
 */
export function niceCeil(val: number): number {
  if (val <= 0) return 1;
  const exp = Math.floor(Math.log10(val));
  const magnitude = Math.pow(10, exp);
  const normalized = val / magnitude;
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

export const formatCompactNumber = (num: number): string => {
  if (num === 0) return "0";

  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  });

  return formatter.format(num);
};

export const shortEnglishHumanizer = (
  value: number,
  {
    delimiter = ", ",
    spacer = " ",
    units = ["y", "mo", "w", "d", "h", "m", "s", "ms"],
    largest = 2,
    round = false,
  }: {
    delimiter?: string;
    spacer?: string;
    units?: Unit[];
    largest?: number;
    round?: boolean;
  } = {},
) => {
  const humanizer = humanizeDuration.humanizer({
    language: "shortEn",
    languages: {
      shortEn: {
        y: () => "y",
        mo: () => "mo",
        w: () => "w",
        d: () => "d",
        h: () => "h",
        m: () => "m",
        s: () => "s",
        ms: () => "ms",
      },
    },
  });

  return humanizer(value, { delimiter, spacer, units, largest, round });
};

/** Compact currency for tight chart labels (e.g. donut center): max 2 decimals, K/M/B suffix. */
export const dollarValueCompactFormatter = (value: number): string => {
  if (!Number.isFinite(value)) {
    return "$0";
  }

  const abs = Math.abs(value);
  if (abs === 0) {
    return "$0";
  }

  if (abs < 1000) {
    return `$${value.toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    })}`;
  }

  return `$${Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(value)}`;
};

export const dollarValueFloorTwoDecimals = (value: number) => {
  const floored = Math.floor(value * 100) / 100;
  return `$${floored.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export function formatSecondsToDuration(seconds: number) {
  if (seconds === 0) {
    return "0s";
  }

  const now = new Date();
  const end = new Date(now.getTime() + seconds * 1000);
  const duration = intervalToDuration({ start: now, end: end });

  const formattedFull = formatDuration(duration)
    .replace(/ years?/, "y")
    .replace(/ months?/, "mo")
    .replace(/ days?/, "d")
    .replace(/ hours?/, "h")
    .replace(/ minutes?/, "m")
    .replace(/ seconds?/, "s");

  const parts = formattedFull.split(" ");

  const limitedParts = parts.slice(0, 2);

  return limitedParts.join(" ");
}

/**
 * Format minutes into a human-readable duration string.
 * - Less than 60 minutes: "Xm"
 * - 1-24 hours: "Xh" or "X.Xh" (1 decimal place if fractional)
 * - 24+ hours: "Xd" or "X.Xd" (1 decimal place if fractional)
 */
export function formatMinutesToDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = minutes / 60;
  if (hours >= 24) {
    const days = hours / 24;
    if (Number.isInteger(days)) {
      return `${days}d`;
    }
    return `${days.toFixed(1)}d`;
  }
  if (Number.isInteger(hours)) {
    return `${hours}h`;
  }
  return `${hours.toFixed(1)}h`;
}

/**
 * Format minutes as a compact string using "min" suffix with SI-style abbreviations.
 * Examples:
 * - 500 → "500 min"
 * - 1200 → "1.2k min"
 * - 120000 → "120k min"
 * - 1500000 → "1.5M min"
 */
export function formatMinutesCompact(minutes: number): string {
  if (minutes >= 1_000_000) {
    const value = minutes / 1_000_000;
    return `${Number.isInteger(value) ? value : value.toFixed(1)}M min`;
  }
  if (minutes >= 1_000) {
    const value = minutes / 1_000;
    return `${Number.isInteger(value) ? value : value.toFixed(1)}k min`;
  }
  return `${Math.round(minutes)} min`;
}

/** Compact step duration, e.g. 138000 → "2m 18s", 20000 → "20s". */
export function formatStepDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

export function formatDurationMs(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  return `${seconds}s`;
}

export const labelsToDateRanges = (): Record<string, DateRange> => {
  const today = DateTime.now().startOf("minute").setZone("utc");
  return {
    "1h": {
      from: today.minus({ hours: 1 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "2h": {
      from: today.minus({ hours: 2 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "4h": {
      from: today.minus({ hours: 4 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "12h": {
      from: today.minus({ hours: 12 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "1D": {
      from: today.minus({ days: 1 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "2D": {
      from: today.minus({ days: 2 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "5D": {
      from: today.minus({ days: 5 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    "14D": {
      from: today.minus({ days: 14 }).toJSDate(),
      to: today.endOf("minute").toJSDate(),
    },
    MTD: {
      from: today.startOf("month").startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    "7D": {
      from: today.minus({ days: 7 }).startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    "30D": {
      from: today.minus({ days: 30 }).startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    "3M": {
      from: today.minus({ months: 3 }).startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    "6M": {
      from: today.minus({ months: 6 }).startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    "12M": {
      from: today.minus({ months: 12 }).startOf("day").toJSDate(),
      to: today.endOf("day").toJSDate(),
    },
    ...calendarMonthTabDateRanges(today),
  };
};

/** Keys `M0`…`M59`: M0 = current calendar month (start → min(today, month end)); M1+ = prior full months.
 * Generated up to 5 years back so dropdowns for accounts with old invoices
 * (Usage v2 month tabs) can resolve their monthTab ranges via `labelsToDateRanges()`. */
function calendarMonthTabDateRanges(
  today: DateTime,
): Record<string, DateRange> {
  const out: Record<string, DateRange> = {};
  for (let i = 0; i < 60; i++) {
    const monthStart = today.startOf("month").minus({ months: i });
    const endCap = DateTime.min(
      monthStart.endOf("month").endOf("day"),
      today.endOf("day"),
    );
    out[`M${i}`] = {
      from: monthStart.startOf("day").toJSDate(),
      to: endCap.toJSDate(),
    };
  }
  return out;
}

export const getNextChartColor = (index: number) => {
  const colors = [
    "var(--chart-1)",
    "var(--chart-2)",
    "var(--chart-3)",
    "var(--chart-4)",
    "var(--chart-5)",
    "var(--chart-6)",
    "var(--chart-7)",
    "var(--chart-8)",
    "var(--chart-9)",
    "var(--chart-10)",
  ];
  return colors[index % colors.length];
};

export async function redirectToPaymentSetupPage(
  api: AxiosInstance,
  activeGithubOrg: string,
) {
  const response = await api.post(
    `/user/github/orgs/${activeGithubOrg}/billing/setup-payment`,
  );
  window.location.href = response.data.url;
}

export async function redirectToBillingPortal(
  api: AxiosInstance,
  activeGithubOrg: string,
) {
  const response = await api.get(
    `/user/github/orgs/${activeGithubOrg}/billing/portal-url`,
  );
  window.location.href = response.data.url;
}

/**
 * Fetch a redirect URL from the backend and open it in a new tab without
 * navigating the current tab.
 *
 * We open the new tab synchronously inside the click handler so popup
 * blockers honor the user gesture, then point it at the signed URL once
 * the API call returns. If the popup is blocked we surface the error
 * rather than silently navigating the current tab, since callers ask for
 * "open in new tab" behavior explicitly.
 */
export async function openApiUrlInNewTab(
  api: AxiosInstance,
  path: string,
  method: "get" | "post" = "get",
): Promise<void> {
  const portalTab = window.open("", "_blank");
  if (!portalTab) {
    throw new Error("Popup blocked: unable to open page in new tab");
  }
  try {
    const response =
      method === "post" ? await api.post(path) : await api.get(path);
    const url: string = response.data.url;
    portalTab.location.href = url;
  } catch (error) {
    if (!portalTab.closed) {
      portalTab.close();
    }
    throw error;
  }
}

export function openBillingPortalInNewTab(
  api: AxiosInstance,
  activeGithubOrg: string,
): Promise<void> {
  return openApiUrlInNewTab(
    api,
    `/user/github/orgs/${activeGithubOrg}/billing/portal-url`,
  );
}

export function openPaymentSetupPageInNewTab(
  api: AxiosInstance,
  activeGithubOrg: string,
): Promise<void> {
  return openApiUrlInNewTab(
    api,
    `/user/github/orgs/${activeGithubOrg}/billing/setup-payment`,
    "post",
  );
}

/**
 * Mint a fresh Lago payment URL for an overdue invoice and open it in a new
 * tab. The URL is generated on demand (it isn't carried on the invoice row),
 * so the tab is opened synchronously to keep the user gesture for popup
 * blockers and then pointed at the signed URL once the API returns.
 */
export function openInvoicePaymentInNewTab(
  api: AxiosInstance,
  activeGithubOrg: string,
  lagoInvoiceId: string,
): Promise<void> {
  return openApiUrlInNewTab(
    api,
    `/user/github/orgs/${activeGithubOrg}/billing/invoices/${encodeURIComponent(
      lagoInvoiceId,
    )}/payment-url`,
  );
}

export function formatBytes(bytes: number) {
  if (bytes === 0) return "0B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(2)}${sizes[i]}`;
}

export function formatTimeAgo(dateTime: DateTime): string {
  if (!dateTime || !dateTime.isValid) return "-";

  const now = DateTime.now();
  const diff = now.diff(dateTime, "seconds");
  const diffInSeconds = Math.floor(diff.seconds);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }

  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)}m ago`;
  }

  if (diffInSeconds < 86400) {
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  }

  const time = dateTime.toLocal().toFormat("h:mma").toLowerCase();

  if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago at ${time}`;
  }

  const months = Math.floor(diffInSeconds / 2592000);
  return `${months}mo ago at ${time}`;
}

export function escapeQueryText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function isAbortError(err: unknown): boolean {
  return !!(
    err &&
    typeof err === "object" &&
    (err as any).name === "AbortError"
  );
}

export function makeAbortError(): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException("Aborted", "AbortError");
  }
  const e = new Error("Aborted");
  (e as any).name = "AbortError";
  return e;
}

/**
 * Format duration for test display
 * - If <1s: show in milliseconds (e.g., "123ms")
 * - If <10s: show with 1 decimal point (e.g., "5.2s")
 * - If <60s: show rounded seconds (e.g., "45s")
 * - Otherwise: show minutes and seconds (e.g., "2m 30s")
 */
export function formatTestDuration(seconds?: number | null): string {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return "—";
  if (seconds < 1) {
    return `${Math.round(seconds * 1000)}ms`;
  }
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  // Round first to avoid edge case where seconds % 60 rounds to 60
  const totalSecs = Math.round(seconds);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

const COMPARISON_PERIOD_FALLBACK =
  "Change compared to the previous period of the same length.";

/**
 * Returns tooltip parts for the analytics "change %" indicator so the date
 * range can be rendered in a pill. Returns { direction, dateRange } or
 * { fallback } when dates are missing.
 */
export function getComparisonPeriodTooltipParts(
  startDate: Date | undefined,
  endDate: Date | undefined,
  changePercentage: number | undefined,
): { direction: string; dateRange: string } | { fallback: string } {
  if (!startDate || !endDate) {
    return { fallback: COMPARISON_PERIOD_FALLBACK };
  }
  const start = DateTime.fromJSDate(startDate).setZone("utc").startOf("day");
  const end = DateTime.fromJSDate(endDate).setZone("utc").startOf("day");
  const periodDays = Math.round(end.diff(start, "days").days) + 1;
  if (periodDays < 1) {
    return { fallback: COMPARISON_PERIOD_FALLBACK };
  }
  const prevEnd = start.minus({ days: 1 });
  const prevStart = start.minus({ days: periodDays });
  const fmt = "MMM d, yyyy";
  const dateRange = `${prevStart.toFormat(fmt)} – ${prevEnd.toFormat(fmt)}`;
  const direction =
    changePercentage === undefined
      ? "Change"
      : changePercentage > 0
        ? "Increase"
        : changePercentage < 0
          ? "Decrease"
          : "No change";
  return { direction, dateRange };
}

/**
 * Minimal store for `useSyncExternalStore`: a snapshot plus change listeners.
 * For imperative code (observers, timers, DOM measurement) that publishes to
 * renders without routing through component state.
 */
export function createSnapshotStore<T>(initial: T) {
  let snapshot = initial;
  const listeners = new Set<() => void>();
  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    set: (next: T) => {
      if (Object.is(next, snapshot)) return;
      snapshot = next;
      listeners.forEach((listener) => listener());
    },
  };
}
export type SnapshotStore<T> = ReturnType<typeof createSnapshotStore<T>>;
